// CatalogSearchTab — Suche-Reiter des Karten-Katalogs: Filterzeile über dem Kartenraster,
// Treffer aus dem gesamten Kartenbestand. Karten, die schon im Deck liegen, bleiben in der
// Liste — die Suche beantwortet „was gibt es", nicht „was fehlt noch".
import React from 'react';
import { RefreshCw, Search, SearchX } from 'lucide-react';
import CardGrid from '../../../ui/CardGrid';
import EmptyState from '../../../ui/EmptyState';
import { errorMessage } from '../../../api/errorMessage';
import { useLoadMoreSentinel } from '../../../hooks/useLoadMoreSentinel';
import CardBrowserFilterRow from '../components/CardBrowserFilterRow';
import { useCardBrowserSearch } from '../hooks/useCardBrowserSearch';
import CatalogCardTile from './CatalogCardTile';
import CatalogSkeletonGrid from './CatalogSkeletonGrid';

/**
 * @param {object} filters — Filter-Objekt; liegt in `CardCatalog`, weil `Tabs.Panel` den
 *   inaktiven Reiter abbaut
 * @param {(patch: object) => void} onFiltersChange — Teil-Änderung des Filter-Objekts
 * @param {boolean} canAdd — Plus-Knöpfe zeigen (eigenes, bearbeitbares Deck)
 * @param {string|null} addingId — Scryfall-ID der Karte, deren Add gerade läuft
 * @param {(entry) => void} onAdd — Plus-Klick
 * @param {(entry) => void} onOpenCard — Klick auf die Kachel (Karten-Detail)
 * @param {boolean} dndEnabled — Kacheln als Drag-Quellen freigeben
 */
export default function CatalogSearchTab({
  filters,
  onFiltersChange,
  canAdd = false,
  addingId = null,
  onAdd,
  onOpenCard,
  dndEnabled = false,
}) {
  const {
    entries,
    totalFilteredCards,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    refetch,
    isEmptyQuery,
  } = useCardBrowserSearch({ filters });

  const sentinelRef = useLoadMoreSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });

  return (
    <div className="card-catalog__tab">
      <CardBrowserFilterRow
        filters={filters}
        onChange={onFiltersChange}
        showOwnedOnly
        showGrouping
        mobileDock={false}
      />

      {isEmptyQuery ? (
        <EmptyState
          icon={Search}
          title="Suche nach Karten"
          description="Name, Suchsyntax oder ein Filter genügt."
        />
      ) : status === 'error' ? (
        <EmptyState
          variant="error"
          icon={RefreshCw}
          title="Karten konnten nicht geladen werden"
          description={errorMessage(error, 'Bitte später erneut versuchen.')}
          onRetry={() => refetch()}
        />
      ) : status === 'pending' ? (
        <CatalogSkeletonGrid label="Karten werden geladen" />
      ) : entries.length === 0 ? (
        <EmptyState
          variant="filter"
          icon={SearchX}
          title="Keine Karten gefunden"
          description="Passe Suche oder Filter an."
        />
      ) : (
        <>
          <CardGrid variant="browse">
            {entries.map((entry) => (
              <CatalogCardTile
                key={entry.id}
                entry={entry}
                canAdd={canAdd}
                adding={addingId === entry.id}
                onAdd={onAdd}
                onOpen={onOpenCard}
                dndEnabled={dndEnabled}
              />
            ))}
          </CardGrid>
          <div ref={sentinelRef} className="card-catalog__sentinel" aria-hidden />
          <div className="card-catalog__count">
            {entries.length} von {totalFilteredCards}
          </div>
        </>
      )}
    </div>
  );
}
